import { useState } from "react";
import scenarios from "./scenarios.json";
import { CheckCircle, XCircle, RotateCcw, MessageCircle } from "lucide-react";

type Scenario = {
  id: number;
  scenario: string;
  answer: string;
  explanation: string;
};

interface Props {
  onComplete?: (score: number, maxScore: number) => void;
}

function CyberbullyDetection({ onComplete }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore]               = useState(0);
  const [feedback, setFeedback]         = useState("");
  const [isCorrect, setIsCorrect]       = useState<boolean | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [gameOver, setGameOver]         = useState(false);

  function handleAnswer(userAnswer: string) {
    const current: Scenario = scenarios[currentIndex];
    const correct = userAnswer === current.answer;
    if (correct) setScore(s => s + 1);
    setIsCorrect(correct);
    setFeedback(current.explanation);
    setShowFeedback(true);
    setTimeout(goToNextQuestion, 2200);
  }

  function goToNextQuestion() {
    const next = currentIndex + 1;
    if (next < scenarios.length) {
      setCurrentIndex(next);
      setShowFeedback(false);
      setIsCorrect(null);
    } else {
      const finalScore = score; // correct answers out of total
      onComplete?.(finalScore, scenarios.length);
      setGameOver(true);
    }
  }

  function restartGame() {
    setCurrentIndex(0);
    setScore(0);
    setFeedback("");
    setIsCorrect(null);
    setShowFeedback(false);
    setGameOver(false);
  }

  const pct = Math.round((currentIndex / scenarios.length) * 100);

  if (gameOver) {
    const perfect = score === scenarios.length;
    return (
      <div className="flex flex-col items-center text-center py-6 gap-4">
        <div className="w-20 h-20 rounded-full flex items-center justify-center text-4xl"
          style={{ background: 'linear-gradient(135deg, #6366f1, #3b82f6)' }}>
          🎉
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            {perfect ? 'Perfect Score!' : 'Game Complete!'}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            You identified <span className="font-bold text-indigo-600">{score}</span> of{' '}
            <span className="font-bold">{scenarios.length}</span> scenarios correctly
          </p>
        </div>
        {/* Score ring */}
        <div className="flex items-center gap-3">
          {[...Array(scenarios.length)].map((_, i) => (
            <div key={i} className={`w-3 h-3 rounded-full ${i < score ? 'bg-emerald-400' : 'bg-slate-200'}`} />
          ))}
        </div>
        <button
          onClick={restartGame}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white transition hover:opacity-90"
          style={{ background: 'linear-gradient(135deg, #6366f1, #3b82f6)', border: 'none', cursor: 'pointer' }}
        >
          <RotateCcw className="w-4 h-4" />
          Play Again
        </button>
      </div>
    );
  }

  const current: Scenario = scenarios[currentIndex];

  return (
    <div className="flex flex-col gap-5">
      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs text-slate-400 mb-1.5">
          <span>Question {currentIndex + 1} of {scenarios.length}</span>
          <span>{score} correct</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #6366f1, #3b82f6)' }}
          />
        </div>
      </div>

      {/* Scenario card */}
      <div className="rounded-2xl p-5 text-center" style={{ background: 'linear-gradient(135deg, #f0f4ff, #e8edff)' }}>
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-3">
          <MessageCircle className="w-5 h-5 text-indigo-600" />
        </div>
        <p className="text-slate-700 font-medium text-base leading-relaxed">
          {current.scenario}
        </p>
        <p className="text-xs text-slate-400 mt-2 font-medium uppercase tracking-wide">Is this cyberbullying?</p>
      </div>

      {/* Feedback or buttons */}
      {showFeedback ? (
        <div
          className="rounded-2xl px-5 py-4 flex items-start gap-3"
          style={{ background: isCorrect ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)' }}
        >
          {isCorrect
            ? <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
            : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          }
          <div>
            <p className="text-sm font-semibold" style={{ color: isCorrect ? '#059669' : '#dc2626' }}>
              {isCorrect ? 'Correct!' : 'Not quite'}
            </p>
            <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{feedback}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleAnswer("Yes")}
            className="py-3 rounded-2xl text-sm font-bold text-white transition hover:opacity-90 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #ef4444, #f97316)', border: 'none', cursor: 'pointer' }}
          >
            Yes, it's bullying
          </button>
          <button
            onClick={() => handleAnswer("No")}
            className="py-3 rounded-2xl text-sm font-bold text-white transition hover:opacity-90 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #10b981, #3b82f6)', border: 'none', cursor: 'pointer' }}
          >
            No, it's fine
          </button>
        </div>
      )}

      {/* Score dots */}
      <div className="flex justify-center gap-2">
        {[...Array(scenarios.length)].map((_, i) => (
          <div key={i} className={`rounded-full transition-all ${
            i < currentIndex ? 'w-2 h-2 bg-indigo-400' :
            i === currentIndex ? 'w-3 h-3 bg-indigo-600' :
            'w-2 h-2 bg-slate-200'
          }`} />
        ))}
      </div>
    </div>
  );
}

export default CyberbullyDetection;
